
/**
 * Attaches a request URL and an access token to an HTTP GET call.
 * @param requestUrl 
 * @param accessToken 
 */
export async function sendGETRequest<TResponse>(requestUrl: string, accessToken: string): Promise<TResponse> {
    const options = createGETRequestOptions(accessToken);
    return await request<TResponse>(requestUrl, options);
}

/**
 * Attaches a request URL, an access token, a request object and an optional abort controller to an HTTP POST call.
 * @param requestUrl 
 * @param accessToken 
 * @param requestObject 
 * @param abortController 
 * @returns 
 */
export async function sendPOSTRequest<TResponse, TRequest>(requestUrl: string, accessToken: string, requestObject: TRequest, abortController?: AbortController): Promise<TResponse> {
    const options = createPOSTRequestOptions<TRequest>(accessToken, requestObject, abortController);
    return await request<TResponse>(requestUrl, options);
}

/**
 * Attaches a request URL, an access token and a request object to an HTTP PUT call.
 * @param requestUrl 
 * @param accessToken 
 * @param requestObject 
 * @returns 
 */
export async function sendPUTRequest<TResponse, TRequest>(requestUrl: string, accessToken: string, requestObject: TRequest): Promise<TResponse> {
    const options = createPUTRequestOptions<TRequest>(accessToken, requestObject);
    return await request<TResponse>(requestUrl, options);
}

/**
 * Attaches a request URL and an access token to an HTTP DELETE call.
 * @param requestUrl
 * @param accessToken
 * @returns
 */
export async function sendDELETERequest<TResponse>(requestUrl: string, accessToken: string): Promise<TResponse> {
    const options = createDELETERequestOptions(accessToken);
    return await request<TResponse>(requestUrl, options);
}

export async function sendBlobGETRequest(requestUrl: string, accessToken: string): Promise<Blob> {
    const response = await fileGETRequest(requestUrl, accessToken);

    if (response.ok) {
        return response.blob();
    }

    throw new Error(response.statusText);
}

async function request<TResponse>(requestUrl: string, options: RequestInit = {}): Promise<TResponse> {
    const response = await fetch(requestUrl, options);
    if (response.ok) {
        try {
            return await response.json().then(data => data as TResponse);
        } catch {
            return {} as unknown as TResponse;
        }
    }

    if (response.status === 400) {
        const errorObject = await response.json() as unknown as TResponse;
        return errorObject;
    }

    throw new Error(response.statusText);
}

function createGETRequestOptions(accessToken: string): RequestInit {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;
    headers.append("Authorization", bearer);
    const options = {
        method: "GET",
        headers: headers
    };
    return options;
}

function createDELETERequestOptions(accessToken: string): RequestInit {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;
    headers.append("Authorization", bearer);
    const options = {
        method: "DELETE",
        headers: headers
    };
    return options;
}

function createPOSTRequestOptions<TRequest>(accessToken: string, requestObject: TRequest, abortController?: AbortController): RequestInit {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;
    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");
    const options = {
        method: "POST",
        headers: headers,
        signal: abortController?.signal,
        body: JSON.stringify(requestObject)
    };
    return options;
}

function createPUTRequestOptions<TRequest>(accessToken: string, requestObject: TRequest): RequestInit {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;
    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");
    const options = {
        method: "PUT",
        headers: headers,
        body: JSON.stringify(requestObject)
    };
    return options;
}

async function fileGETRequest(requestUrl: string, accessToken: string): Promise<Response> {
    return await fetch(requestUrl, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${accessToken}`,
            binaryStringResponseBody: "true"
        }
    });
}